<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    valueA?: string;
    valueB?: string;
    labelA?: string;
    labelB?: string;
    disabled?: boolean;
    ariaLabel?: string;
    name?: string;
  }>(),
  {
    valueA: 'a',
    valueB: 'b',
    labelA: 'A',
    labelB: 'B',
    ariaLabel: 'Segment control',
    name: 'segment-control',
  }
);

const emits = defineEmits<{ (e: 'update:modelValue', v: string): void }>();

const idA = computed(() => `segment-option-${props.valueA}`);
const idB = computed(() => `segment-option-${props.valueB}`);
</script>
<template>
  <div class="seg" role="radiogroup" :aria-label="ariaLabel">
    <input
      :id="idA"
      class="seg__input"
      type="radio"
      :name="name"
      :value="valueA"
      :checked="modelValue === valueA"
      :disabled="disabled"
      @change="$emit('update:modelValue', valueA)"
    />
    <input
      :id="idB"
      class="seg__input"
      type="radio"
      :name="name"
      :value="valueB"
      :checked="modelValue === valueB"
      :disabled="disabled"
      @change="$emit('update:modelValue', valueB)"
    />

    <div
      class="seg__track"
      :class="{
        'is-a': modelValue === valueA,
        'is-b': modelValue === valueB,
        'is-disabled': disabled,
      }"
    >
      <div class="seg__thumb" aria-hidden="true" />

      <label class="seg__item" :for="idA">
        <span class="seg__text">{{ labelA }}</span>
      </label>
      <label class="seg__item" :for="idB">
        <span class="seg__text">{{ labelB }}</span>
      </label>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.seg {
  display: inline-block;
}

.seg__input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
  pointer-events: none;
}

.seg__track {
  position: relative;
  width: 338px;
  height: 48px;
  padding: 4px;
  border-radius: 12px;
  background: #eef2f7;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  overflow: hidden;

  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.seg__thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: 40px;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 2px 4px 0px #0000000d;
  transition: transform 150ms ease;
}

.seg__track.is-a .seg__thumb {
  transform: translateX(0);
}
.seg__track.is-b .seg__thumb {
  transform: translateX(100%);
}

.seg__item {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  cursor: pointer;
  border-radius: 10px;
  user-select: none;
}

.seg__text {
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  padding: 0 10px;
  color: $textSecondary;
}

.seg__track.is-a .seg__item:first-of-type .seg__text,
.seg__track.is-b .seg__item:last-of-type .seg__text {
  color: $textDefault;
}
</style>
